import { composeSchema } from '@plone/volto/helpers/Extensions';
import { ImageOverlaySchema } from './components/schema/image_overlay';
import { ListStyleSchema } from './components/schema/list_style';
import TextBlockView from './components/TextBlockView/TextBlockView';
import {
  TagWhitelistWidget,
  WhitelistCheckboxFacet,
  WhitelistSelectFacet,
} from './components/search';
import { applyStyleWrapperToBlock } from '@eeacms/volto-block-style';

// INTERAKTIV CODE START//
import AlignWidget from './customizations/volto/components/manage/Widgets/AlignWidget';

import NewsVariation from './components/eeacms_volto-listing-block/variations/NewsVariation';
import { newsSchemaEnhancer } from './components/eeacms_volto-listing-block/variations/newsSchemaEnhancer';
import EventsVariation from './components/eeacms_volto-listing-block/variations/EventsVariation';
import { eventsSchemaEnhancer } from './components/eeacms_volto-listing-block/variations/eventsSchemaEnhancer';
import ResearchVariation from './components/eeacms_volto-listing-block/variations/ResearchVariation';
import { researchSchemaEnhancer } from './components/eeacms_volto-listing-block/variations/researchSchemaEnhancer';
import GridVariation from './components/eeacms_volto-listing-block/variations/GridVariation';
import { gridSchemaEnhancer } from './components/eeacms_volto-listing-block/variations/gridSchemaEnhancer';

// FCMH: Slider Schema Enhancer
import sliderSchemaEnhancer from './customizations/@kitconcept/volto-slider-block/components/sliderSchemaEnhancer';

// Custom Block: CTASection
import CTASectionView from './components/theme/Blocks/CTASection/View';
import CTASectionEdit from './components/theme/Blocks/CTASection/Edit';
import { bullhornSVG } from './helpers/icons';
import ColorSelectWidget from './components/theme/Blocks/CTASection/ColorSelectWidget';

// Custom Block: Welcome
import WelcomeView from './components/theme/Blocks/Welcome/View';
import WelcomeEdit from './components/theme/Blocks/Welcome/Edit';
import { welcomeSVG } from './helpers/icons';

// Custom Block: Header
import HeaderView from './components/theme/Blocks/Header/View';
import HeaderEdit from './components/theme/Blocks/Header/Edit';
import { headerSVG } from './helpers/icons';

// Custom Block: Contact
import ContactEdit from './components/theme/Blocks/Contact/Edit';
import ContactView from './components/theme/Blocks/Contact/View';
import { contactSVG } from './helpers/icons';

// Custom Block: Quote
import QuoteView from './components/theme/Blocks/Quote/View';
import QuoteEdit from './components/theme/Blocks/Quote/Edit';
import { quoteSVG } from './helpers/icons';

// Custom Block FCMH-Teaser
import TeaserView from './components/theme/Blocks/Teaser/View';
import TeaserEdit from './components/theme/Blocks/Teaser/Edit';
import { fcmhteaserSVG } from './helpers/icons';

// Custom Block FCMH-Statistics
import StatisticsView from './components/theme/Blocks/Statistics/View';
import StatisticsEdit from './components/theme/Blocks/Statistics/Edit';
import { fcmhstatisticsSVG } from './helpers/icons';

// Custom Block FCMH-Timeline
import TimelineView from './components/theme/Blocks/Timeline/View';
import TimelineEdit from './components/theme/Blocks/Timeline/Edit';
import { timelineSVG } from './helpers/icons';

// Custom Block: Brand
import BrandView from './components/theme/Blocks/Brand/View';
import BrandEdit from './components/theme/Blocks/Brand/Edit';
import { brandSVG } from './helpers/icons';

// Custom Block FCMH-Highlight
import HighlightView from './components/theme/Blocks/Highlight/View';
import HighlightEdit from './components/theme/Blocks/Highlight/Edit';
import { highlightSVG } from './helpers/icons';

// INTERAKTIV CODE END //

const applyConfig = (config) => {
  // Use environment variable if set (for testing), otherwise use default
  config.settings.devProxyToApiPath =
    process.env.RAZZLE_DEV_PROXY_API_PATH || 'http://localhost:8080/fcmh';
  // config.settings.siteLabel = 'FCMH';
  config.settings.apiPath =
    process.env.RAZZLE_API_PATH || 'http://localhost:3000';
  config.settings.internalApiPath =
    process.env.RAZZLE_INTERNAL_API_PATH || 'http://127.0.0.1:8080/fcmh';
  config.settings.isMultilingual = true;
  config.settings.supportedLanguages = ['en', 'de'];
  config.settings.defaultLanguage = 'de';
  config.settings.navDepth = 3;
  config.views.contentTypesViews.Contact = ContactView;

  // config.blocks.blocksConfig.gridBlock.allowedBlocks.push('countUpBlock');
  config.blocks.themes = [
    {
      style: {
        '--theme-color': '#FFFFFF',
        '--theme-foreground-color': '#101010',
        '--theme-high-contrast-color': '#FFFFFF',
        '--theme-low-contrast-foreground-color': '#DDDDDD',
      },
      name: 'default',
      label: 'Default',
    },
    {
      style: {
        '--theme-color': '#f4f4f4',
        '--theme-foreground-color': '#101010',
        '--theme-high-contrast-color': '#FFFFFF',
        '--theme-low-contrast-foreground-color': '##f4f4f4',
      },
      name: 'default-grey',
      label: 'Default Grey',
    },
    {
      style: {
        '--theme-color': '#00859b',
        '--theme-foreground-color': '#FFFFFF',
        '--theme-high-contrast-color': '#00859b',
        '--theme-low-contrast-foreground-color': '#FFFFFF',
        '--link-foreground-color': '#FFFFFF',
      },
      name: 'highlighted',
      label: 'Highlighted',
    },
    {
      style: {
        '--theme-color': '#e8a817',
        '--theme-foreground-color': '#101010',
        '--theme-high-contrast-color': '#e8a817',
        '--theme-low-contrast-foreground-color': '#FFFFFF',
        '--link-foreground-color': '#FFFFFF',
      },
      name: 'highlighted-yellow',
      label: 'Highlighted Yellow',
    },
  ];

  // INTERAKTIV CODE START//
  // Remove blocks.themes from button block
  const BUTTON_BLOCK_ID = '__button';

  const prevEnhancer =
    config.blocks.blocksConfig[BUTTON_BLOCK_ID]?.schemaEnhancer;

  config.blocks.blocksConfig[BUTTON_BLOCK_ID] = {
    ...config.blocks.blocksConfig[BUTTON_BLOCK_ID],
    schemaEnhancer: (schema, formData, intl) => {
      const s = prevEnhancer ? prevEnhancer(schema, formData, intl) : schema;

      delete s.properties?.style;
      delete s.properties?.styles;
      delete s.properties?.theme;

      if (Array.isArray(s.fieldsets)) {
        s.fieldsets = s.fieldsets.map((fs) => ({
          ...fs,
          fields: fs.fields.filter(
            (f) => !['style', 'styles', 'theme'].includes(f),
          ),
        }));
      }

      return s;
    },
  };
  // INTERAKTIV CODE END//

  // --- GRID BLOCK SETTINGS ---
  config.blocks.blocksConfig.gridBlock = {
    ...config.blocks.blocksConfig.gridBlock,
    schemaEnhancer: composeSchema(
      config.blocks.blocksConfig.gridBlock?.schemaEnhancer ||
        ((args) => args.schema),
      ListStyleSchema,
    ),
    gridAllowedBlocks: [
      ...(config.blocks.blocksConfig.gridBlock?.gridAllowedBlocks || [
        'teaser',
        'image',
      ]),
      'slate',
      'heading',
      'listing',
    ],
    themes: [
      {
        style: {
          '--theme-color': '#FFFFFF',
          '--theme-foreground-color': '#4F4E4E',
          '--theme-high-contrast-color': '#FFFFFF',
          '--theme-low-contrast-foreground-color': '#DDDDDD',
        },
        name: 'default',
        label: 'Default',
      },
      {
        style: {
          '--theme-color': '#f4f4f4',
          '--theme-foreground-color': '#111111',
          '--theme-high-contrast-color': '#f4f4f4',
          '--theme-low-contrast-foreground-color': '#111111',
          '--link-foreground-color': '#00859b',
        },
        name: 'default-grey',
        label: 'Default Grey',
      },
      {
        style: {
          '--theme-color': '#00859b',
          '--theme-foreground-color': '#FFFFFF',
          '--theme-high-contrast-color': '#00859b',
          '--theme-low-contrast-foreground-color': '#FFFFFF',
          '--link-foreground-color': '#FFFFFF',
        },
        name: 'highlighted',
        label: 'Highlighted',
      },
      {
        style: {
          '--theme-color': '#e8a817',
          '--theme-foreground-color': '#FFFFFF',
          '--theme-high-contrast-color': '#e8a817',
          '--theme-low-contrast-foreground-color': '#FFFFFF',
          '--link-foreground-color': '#FFFFFF',
        },
        name: 'highlighted-yellow',
        label: 'Highlighted Yellow',
      },
    ],
    blocksConfig: {
      slate: {
        ...config.blocks.blocksConfig.slate,
        view: TextBlockView,
      },
      image: config.blocks.blocksConfig.image,
      teaser: config.blocks.blocksConfig.teaser,
      heading: config.blocks.blocksConfig.heading,
      listing: config.blocks.blocksConfig.listing,
    },
  };

  // INTERAKTIV CODE START//
  // Default link font-color
  const defaultTheme =
    config.blocks.themes?.find((t) => t.name === 'default') ||
    config.blocks.themes?.[0];

  if (defaultTheme) {
    defaultTheme.style['--link-foreground-color'] = '#00859b';
  }
  // INTERAKTIV CODE END//

  // Apply ListStyleSchema to the slate block
  if (config.blocks.blocksConfig.slate) {
    const originalSlateEnhancer =
      config.blocks.blocksConfig.slate.schemaEnhancer;
    config.blocks.blocksConfig.slate.schemaEnhancer = composeSchema(
      originalSlateEnhancer || ((args) => args.schema),
      ListStyleSchema,
    );
    config.blocks.blocksConfig.slate.view = TextBlockView;
    config.blocks.blocksConfig.slate = applyStyleWrapperToBlock(
      config.blocks.blocksConfig.slate,
    );
  }

  // --- IMAGE OVERLAY ENHANCERS ---
  config.blocks.blocksConfig.teaser.schemaEnhancer = composeSchema(
    config.blocks.blocksConfig.teaser.schemaEnhancer,
    ImageOverlaySchema,
  );
  config.blocks.blocksConfig.listing.schemaEnhancer = composeSchema(
    config.blocks.blocksConfig.listing.schemaEnhancer,
    ImageOverlaySchema,
  );
  config.blocks.blocksConfig.image.schemaEnhancer = composeSchema(
    config.blocks.blocksConfig.image.schemaEnhancer,
    ImageOverlaySchema,
  );

  // --- SEARCH FACETS ---
  config.widgets.widget.tagWhitelist = TagWhitelistWidget;
  config.blocks.blocksConfig.search.extensions.facetWidgets.types = [
    ...config.blocks.blocksConfig.search.extensions.facetWidgets.types.map(
      (facetWidget) => {
        if (facetWidget.id === 'checkboxFacet') {
          return {
            ...facetWidget,
            view: WhitelistCheckboxFacet,
            schemaEnhancer: WhitelistCheckboxFacet.schemaEnhancer,
            stateToValue: WhitelistCheckboxFacet.stateToValue,
            valueToQuery: WhitelistCheckboxFacet.valueToQuery,
          };
        }
        if (facetWidget.id === 'selectFacet') {
          return {
            ...facetWidget,
            view: WhitelistSelectFacet,
            schemaEnhancer: WhitelistSelectFacet.schemaEnhancer,
            stateToValue: WhitelistSelectFacet.stateToValue,
            valueToQuery: WhitelistSelectFacet.valueToQuery,
          };
        }
        return facetWidget;
      },
    ),
  ];

  // --- IMAGE ASPECT RATIOS ---
  config.settings.image_crop_aspect_ratios = [
    { label: '16:9', ratio: 16 / 9 },
    { label: '3:2', ratio: 3 / 2 },
    { label: '4:3', ratio: 4 / 3 },
    { label: '1:1', ratio: 1 },
  ];

  // INTERAKTIV CODE START//
  config.settings.integratesBlockStyles = (
    config.settings.integratesBlockStyles || []
  ).filter((type) => type !== 'slate');

  // --- BLOCK WIDTH SCHEMA (slate/heading/accordion) ---
  const BLOCK_WIDTH_SCHEMA = {
    widget: 'align',
    actions: ['narrow', 'normal', 'wide', 'full'],
    defaultAction: 'normal',
  };
  const BLOCK_WIDTH_DEFAULT_SCHEMA = { blockWidth: 'normal' };

  const addNewStyleSchema = (
    schema,
    newField,
    newFieldSchema,
    defaultStyles,
  ) => {
    if (!schema.properties.styles) {
      schema.fieldsets.push({
        id: 'styling',
        title: 'Styling',
        fields: ['styles'],
      });
      schema.properties = {
        ...schema.properties,
        styles: {
          schema: {
            fieldsets: [{ id: 'default', title: 'default', fields: [] }],
            properties: {},
          },
          widget: 'object',
          title: 'styling',
        },
      };
    }
    schema.properties.styles.schema.fieldsets[0].fields = [
      newField,
      ...schema.properties.styles.schema.fieldsets[0].fields,
    ];
    schema.properties.styles.schema.properties[newField] = {
      ...newFieldSchema,
      title: newField,
    };
    if (defaultStyles) {
      schema.properties.styles.default = { ...defaultStyles };
    }
    return schema;
  };

  [
    'slate',
    'heading',
    'accordion',
    'dividerBlock',
    '__button',
    'columnsBlock',
  ].forEach((blockId) => {
    if (config.blocks.blocksConfig[blockId]) {
      const oldEnhancer = config.blocks.blocksConfig[blockId].schemaEnhancer;

      config.blocks.blocksConfig[blockId].schemaEnhancer = composeSchema(
        oldEnhancer,
        ({ schema }) => {
          const s = addNewStyleSchema(
            schema,
            'blockWidth',
            BLOCK_WIDTH_SCHEMA,
            BLOCK_WIDTH_DEFAULT_SCHEMA,
          );

          // Accordion: Theme-Feld im Styling-Tab auf Color-Widget umbiegen
          if (
            blockId === 'accordion' &&
            s.properties &&
            s.properties.styles &&
            s.properties.styles.schema &&
            s.properties.styles.schema.properties &&
            s.properties.styles.schema.properties.theme
          ) {
            s.properties.styles.schema.properties.theme.widget =
              'fcmhColorSelect';
            s.properties.styles.schema.properties.theme.type = 'string';
          }

          return s;
        },
      );
    }
  });

  config.widgets.widget.align = AlignWidget;

  // Custom Widget for CTASection (Key: 'fcmhColorSelect')
  config.widgets = config.widgets || {};
  config.widgets.widget = config.widgets.widget || {};
  config.widgets.widget.fcmhColorSelect = ColorSelectWidget;

  // --- CUSTOM BLOCK: CTASection ---
  config.blocks.blocksConfig.ctasection = {
    id: 'ctasection',
    title: 'FCMH: CTA',
    icon: bullhornSVG,
    group: 'common',
    view: CTASectionView,
    edit: CTASectionEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  // --- CUSTOM BLOCK: Welcome ---
  config.blocks.blocksConfig.welcome = {
    id: 'welcome',
    title: 'FCMH: Welcome',
    icon: welcomeSVG,
    group: 'common',
    view: WelcomeView,
    edit: WelcomeEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  // --- CUSTOM BLOCK: Header ---
  config.blocks.blocksConfig.headerblock = {
    id: 'headerblock',
    title: 'FCMH: Header',
    icon: headerSVG,
    group: 'common',
    view: HeaderView,
    edit: HeaderEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  // --- CUSTOM BLOCK: Contact-Block ---
  config.blocks.blocksConfig.contact = {
    id: 'contact',
    title: 'FCMH: Contact',
    icon: contactSVG,
    group: 'common',
    view: ContactView,
    edit: ContactEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  // --- CUSTOM BLOCK: Quote ---
  config.blocks.blocksConfig.quote = {
    id: 'quote',
    title: 'FCMH: Quote',
    icon: quoteSVG,
    group: 'common',
    view: QuoteView,
    edit: QuoteEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  // --- CUSTOM BLOCK: FCMH-Teaser ---
  config.blocks.blocksConfig.fcmhTeaser = {
    id: 'fcmhTeaser',
    title: 'FCMH: Teaser',
    icon: fcmhteaserSVG,
    group: 'common',
    view: TeaserView,
    edit: TeaserEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  // --- CUSTOM BLOCK: FCMH-Statistics ---
  config.blocks.blocksConfig.fcmhStatistics = {
    id: 'fcmhStatistics',
    title: 'FCMH: Statistics',
    icon: fcmhstatisticsSVG,
    group: 'common',
    view: StatisticsView,
    edit: StatisticsEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  // --- CUSTOM BLOCK: FCMH-Timeline ---
  config.blocks.blocksConfig.fcmhTimeline = {
    id: 'fcmhTimeline',
    title: 'FCMH: Timeline',
    icon: timelineSVG,
    group: 'common',
    view: TimelineView,
    edit: TimelineEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  // --- CUSTOM BLOCK: FCMH-Highlight ---
  config.blocks.blocksConfig.fcmhHighlight = {
    id: 'fcmhHighlight',
    title: 'FCMH: Highlight',
    icon: highlightSVG,
    group: 'common',
    view: HighlightView,
    edit: HighlightEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  // --- CUSTOM BLOCK: Brand ---
  config.blocks.blocksConfig.brand = {
    id: 'brand',
    title: 'FCMH: Brand',
    icon: brandSVG,
    group: 'common',
    view: BrandView,
    edit: BrandEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  // --- CUSTOM LISTING VARIATIONS ---
  // News variation
  if (config.blocks.blocksConfig.listing) {
    config.blocks.blocksConfig.listing.variations = [
      ...(config.blocks.blocksConfig.listing.variations || []),
      {
        id: 'news',
        title: 'FCMH: News',
        template: NewsVariation,
        schemaEnhancer: newsSchemaEnhancer,
      },
    ];
  }

  // Events variation
  if (config.blocks.blocksConfig.listing) {
    config.blocks.blocksConfig.listing.variations = [
      ...(config.blocks.blocksConfig.listing.variations || []),
      {
        id: 'events',
        title: 'FCMH: Events',
        isDefault: false,
        template: EventsVariation,
        schemaEnhancer: eventsSchemaEnhancer,
      },
    ];
  }

  // Resarch Listing Variation
  if (config.blocks.blocksConfig.listing) {
    config.blocks.blocksConfig.listing.variations = [
      ...(config.blocks.blocksConfig.listing.variations || []),
      {
        id: 'research',
        title: 'FCMH: Research',
        template: ResearchVariation,
        schemaEnhancer: researchSchemaEnhancer,
      },
    ];
  }

  // Grid Listing Variation
  if (config.blocks.blocksConfig.listing) {
    config.blocks.blocksConfig.listing.variations = [
      ...(config.blocks.blocksConfig.listing.variations || []),
      {
        id: 'fcmhGrid',
        title: 'FCMH: Grid',
        isDefault: false,
        template: GridVariation,
        schemaEnhancer: gridSchemaEnhancer,
      },
    ];
  }

  // --- SLIDER SCHEMA ENHANCER (FCMH) ---
  const sliderCandidates = ['sliderBlock', 'slider'];
  sliderCandidates.forEach((id) => {
    const blockConf = config.blocks.blocksConfig?.[id];
    if (!blockConf) return;
    blockConf.schemaEnhancer = composeSchema(
      blockConf.schemaEnhancer,
      sliderSchemaEnhancer,
    );
  });

  // INTERAKTIV CODE END//

  return config;
};

export default applyConfig;
